import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, Upload, X } from "lucide-react";

export default function FiscalReps() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [vat, setVat] = useState("");
  const [eori, setEori] = useState("");
  const [bulk, setBulk] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  const { data = [] } = useQuery({
    queryKey: ["fiscal-reps"],
    queryFn: async () => (await api.get("/fiscal-reps")).data,
  });

  const add = useMutation({
    mutationFn: async () => (await api.post("/fiscal-reps", { company_name: name, vat, eori })).data,
    onSuccess: () => { toast.success("Customer added"); setName(""); setVat(""); setEori(""); qc.invalidateQueries({ queryKey: ["fiscal-reps"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/fiscal-reps/${id}`)).data,
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["fiscal-reps"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const bulkImport = useMutation({
    mutationFn: async () => {
      const items = bulk.split("\n").map((line) => {
        const parts = line.split(/[,;\t]/).map((s) => s.trim());
        if (!parts[0]) return null;
        return { company_name: parts[0], vat: parts[1] || null, eori: parts[2] || null };
      }).filter(Boolean);
      return (await api.post("/fiscal-reps/import", { items })).data;
    },
    onSuccess: (d) => { toast.success(`Imported ${d.imported} (total ${d.total})`); setBulk(""); setShowBulk(false); qc.invalidateQueries({ queryKey: ["fiscal-reps"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3">
        <Landmark className="h-5 w-5 text-blue-400" />
        <h1 className="font-heading text-lg font-semibold tracking-tight">Fiscal Representation</h1>
        <span className="text-xs font-mono text-zinc-500">{data.length} customers we represent in Slovenia</span>
        <button data-testid="bulk-import-btn" onClick={() => setShowBulk((v) => !v)}
          className="ml-auto flex items-center gap-2 text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-900 transition-colors duration-150">
          <Upload className="h-4 w-4" /> Bulk import
        </button>
      </header>

      <div className="p-6 space-y-4 max-w-4xl">
        {showBulk && (
          <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/40" data-testid="bulk-panel">
            <p className="text-xs text-zinc-400 mb-2">Paste one customer per line: <span className="font-mono text-zinc-300">Company name, VAT, EORI</span> (VAT/EORI optional).</p>
            <textarea data-testid="bulk-textarea" value={bulk} onChange={(e) => setBulk(e.target.value)} rows={8}
              placeholder={"EP HOLDING d.o.o., SI74158660\nWPC Szakuzletek Kft., HU32506495"}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs font-mono text-zinc-200 focus:border-blue-500 outline-none" />
            <div className="flex gap-2 mt-2">
              <button data-testid="bulk-submit-btn" disabled={bulkImport.isPending || !bulk.trim()} onClick={() => bulkImport.mutate()}
                className="text-sm px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-sm disabled:opacity-50">Import</button>
              <button onClick={() => setShowBulk(false)} className="text-sm px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-900">Cancel</button>
            </div>
          </div>
        )}

        <div className="border border-zinc-800 rounded-sm p-4 flex flex-wrap items-end gap-3" data-testid="add-rep-form">
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500">Company name</label>
            <input data-testid="rep-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="EP HOLDING d.o.o."
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm text-zinc-200 focus:border-blue-500 outline-none" />
          </div>
          <div className="flex flex-col gap-1 w-40">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500">VAT</label>
            <input data-testid="rep-vat-input" value={vat} onChange={(e) => setVat(e.target.value)} placeholder="SI74158660"
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm font-mono text-zinc-200 focus:border-blue-500 outline-none" />
          </div>
          <div className="flex flex-col gap-1 w-40">
            <label className="text-[10px] uppercase tracking-wide text-zinc-500">EORI (optional)</label>
            <input data-testid="rep-eori-input" value={eori} onChange={(e) => setEori(e.target.value)} placeholder="SI74158660"
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm font-mono text-zinc-200 focus:border-blue-500 outline-none" />
          </div>
          <button data-testid="add-rep-btn" disabled={add.isPending || !name.trim()} onClick={() => add.mutate()}
            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-sm disabled:opacity-50">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>

        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">Company</th>
                <th className="text-left font-medium py-2 px-3">VAT</th>
                <th className="text-left font-medium py-2 px-3">EORI</th>
                <th className="py-2 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id} data-testid={`rep-row-${r.id}`} className="border-t border-zinc-800/60 hover:bg-zinc-900 transition-colors duration-150">
                  <td className="py-2 px-3 text-zinc-200">{r.company_name}</td>
                  <td className="py-2 px-3 font-mono text-zinc-400 text-xs">{r.vat || "—"}</td>
                  <td className="py-2 px-3 font-mono text-zinc-400 text-xs">{r.eori || "—"}</td>
                  <td className="py-2 px-3">
                    <button data-testid={`rep-delete-${r.id}`} onClick={() => del.mutate(r.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={4} className="py-16 text-center text-zinc-500">
                  <Landmark className="h-8 w-8 mx-auto mb-3 text-zinc-700" />
                  <p className="text-sm">No customers yet. Add companies you are fiscal representative for.</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
